'use strict';

/* global window, document */
/* eslint
    object-shorthand: off,
    no-param-reassign: off,
    prefer-destructuring: off,
    no-underscore-dangle: off,
    no-var: off,
    prefer-rest-params: off,
    prefer-template: off,
    vars-on-top: off,
    prefer-arrow-callback: off,
    func-names: off,
*/

var _isFunction = require('lodash/isFunction');
var _assign = require('lodash/assign');
var _merge = require('lodash/merge');
var _uuid = require('uuid/v4');
var nativeApi = require('./native-api');

exports.getGA = getGA;
exports.identifyUser = identifyUser;
exports.isInitialized = isInitialized;
exports.isZillowAnalyticsInitialized = isZillowAnalyticsInitialized;
exports.trackEvent = trackEvent;
exports.trackPageview = trackPageview;
exports.setCustomDimensions = setCustomDimensions;
exports.requestUAScript = requestUAScript;
exports.gaCommand = gaCommand;
exports.initGlobalClickListener = initGlobalClickListener;
exports.ecAddImpression = ecAddImpression;
exports.ecAddProduct = ecAddProduct;
exports.trackEventV2 = trackEventV2;
exports.trackPageviewV2 = trackPageviewV2;

var BUTTON_LEFT = 0;
var BUTTON_MIDDLE = 1;

var _initialized = false;
var _globalName = 'ga';

// Max retry count and timeout for ZA function calls
var ZA_RETRY_MAX_COUNT = 2;
var ZA_RETRY_AFTER_MS = 500;

function logInfo() {
    if (typeof window !== 'undefined' && window.console && window.console.info) {
        window.console.info.apply(console, arguments);
    }
}

function logWarn() {
    if (typeof window !== 'undefined' && window.console && window.console.warn) {
        window.console.warn.apply(console, arguments);
    }
}

// get nearest parent element matching selector
// http://stackoverflow.com/questions/15329167/closest-ancestor-matching-selector-using-native-dom
function closest(el, selector, stop) {
    var matchesSelector =
        el.matches || el.webkitMatchesSelector || el.mozMatchesSelector || el.msMatchesSelector;

    var element = el;
    while (element) {
        if (matchesSelector.call(element, selector)) {
            break;
        } else if (stop && element === stop) {
            return null;
        }

        element = element.parentElement;
    }

    return element;
}

/**
 * Add event delegation to the given element.
 *
 * @param root The root element for delegation
 * @param event The event name to delegate
 * @param callback The callback function, will be passed the native event as the first
 *     argument, and the target element matching the selector as the second
 * @param selector The selector to match for the event
 */
function delegate(root, event, callback, selector) {
    function onDelegate(e) {
        var element = closest(e.target, selector, e.currentTarget);

        if (element) {
            callback(e, element);
        }
    }

    root.addEventListener(event, onDelegate);
}

/**
 * Check the node for the given data attribute. If the attribute exists
 * with the value of '!inherit', then it will search up the DOM until it
 * finds a parent with the data attribute.
 *
 * @method _getDataAttribute
 * @private
 * @param {Node} node The node to search
 * @param {String} attr The data attribute to find, e.g. 'za-label'
 * @param {String} [defaultVal] A default value for the attribute
 * @return {String} The data attribute value, or undefined
 */
function _getDataAttribute(node, attr, defaultVal) {
    var attrName = 'data-' + attr;
    var attrValue = defaultVal;
    var ancestor;

    if (node && node.hasAttribute(attrName)) {
        attrValue = node.getAttribute(attrName);
    }

    if (attrValue === '!inherit') {
        ancestor = closest(node.parentElement, '[' + attrName + ']');

        return _getDataAttribute(ancestor, attr);
    }

    return attrValue;
}

/**
 * Get the category from data attributes.
 *
 * @method _getCategory
 * @private
 * @param {Node} node The node to get the value from
 * @return {String} The category string
 */
function _getCategory(node) {
    return _getDataAttribute(node, 'za-category');
}

/**
 * Get the action from data attributes.
 *
 * @method _getAction
 * @private
 * @param {Node} node The node to get the value from
 * @return {String} The action string
 */
function _getAction(node) {
    return _getDataAttribute(node, 'za-action');
}

/**
 * Get the label from data attributes.
 *
 * @method _getLabel
 * @private
 * @param {Node} node The node to get the value from
 * @return {String} The label string
 */
function _getLabel(node) {
    return _getDataAttribute(node, 'za-label');
}

/**
 * Parse the value integer from data attributes.
 *
 * @method _getValue
 * @private
 * @param {Node} node The node to get the value from
 * @return {Int} The value integer, or undefined
 */
function _getValue(node) {
    var value = _getDataAttribute(node, 'za-value');
    return parseInt(value, 10) || undefined;
}

/**
 * Parse the noninteraction value from data attributes.
 *
 * @method _getNoninteraction
 * @private
 * @param {Node} node The node to get the value from
 * @return {Boolean} The noninteraction value, or undefined
 */
function _getNoninteraction(node) {
    var value = _getDataAttribute(node, 'za-noninteraction');

    if (value === 'false') {
        return false;
    }

    if (value === 'true') {
        return true;
    }

    return undefined;
}

/**
 * Get the href from the data attribute or from anchor.
 *
 * @method _getHref
 * @private
 * @param {Node} node The node to get the value from
 * @return {String} The href value
 */
function _getHref(node) {
    var dataHref = _getDataAttribute(node, 'za-href');

    if (typeof dataHref !== 'undefined' && dataHref !== null) {
        if (dataHref === '!ignore') {
            return '';
        }
        return dataHref;
    }
    if (node.hasAttribute('href')) {
        return node.getAttribute('href');
    }
    return undefined;
}

/**
 * Get ua dimension info from the data attribute of node (RE-8249)
 * Dimension 53: Event Details
 * Dimension 54: Event Content
 *
 * @method _getDimensions
 * @private
 * @param {Node} node The node to get the value from
 * @return {Object} Object with key:value pairs that map to ua dimensions:values
 */
function _getDimensions(node) {
    var dataEventDetailsIndex = '53';
    var dataEventDetails = _getDataAttribute(node, 'za-event-details');
    var dataEventContentIndex = '54';
    var dataEventContent = _getDataAttribute(node, 'za-event-content');
    var dimensions = {};

    if (dataEventDetails) {
        dimensions[dataEventDetailsIndex] = dataEventDetails;
    }

    if (dataEventContent) {
        dimensions[dataEventContentIndex] = dataEventContent;
    }

    return dimensions;
}

/**
 * Get the appropriate URL target, making sure to check for ctrl/middle
 * clicks that would open links in a new window.
 *
 * @method _getTarget
 * @private
 * @param {Event} e
 * @param {Node} node The node that was clicked
 * @return {String} The URL target, or `undefined` if none is explicitly
 *     defined.
 */
function _getTarget(e, node) {
    var target;

    if (e) {
        if (node) {
            // Data attribute takes precedence over anchor target
            target = _getDataAttribute(node, 'za-target');
            if ((typeof target === 'undefined' || target === null) && node.hasAttribute('target')) {
                target = node.getAttribute('target');
            }
        }

        // Check for click behavior
        if (
            (e.button === BUTTON_MIDDLE && node.nodeName === 'A') ||
            (e.button === BUTTON_LEFT && e.ctrlKey) ||
            (e.button === BUTTON_LEFT && e.metaKey)
        ) {
            target = '_blank';
        }
    }

    return target;
}

/**
 * In most cases, this function will be called by the global click
 * listener set up by `initGlobalClickListener`.
 * The function can also be called itself in the case
 * where additional logic is required during the click event.
 *
 * The basic idea is to wrap `trackEvent`
 * functionality such that you can track events directly
 * from the DOM. Clicking on an element with class `za-track-event` will
 * trigger the event. You can define `trackEvent`
 * parameters using data attributes on the element, e.g.
 * `data-za-category`, `data-za-action`.
 *
 * Data attributes values are generally defined on the element with the
 * `za-track-event` class, but they can also be configured to inherit
 * the value by using the `!inherit` keyword. When inherit is enabled
 * for an attribute, the value will be pulled from the nearest ancestor
 * that has the same data attribute defined.
 *
 * When the `href` parameter is defined, the URL `target` parameter will
 * be taken directly from the link when applicable or pulled from the
 * data attribute `data-za-target`. In the case that the element was
 * control or middle clicked, the target will automatically be set to
 * `_blank` (middle click will only work for anchor elements).
 *
 * ### HTML Example
 *
 *     <!-- Track event before redirecting. -->
 *     <a href="/mortgage-rates/"
 *         class="za-track-event"
 *         data-za-category="Mortgages"
 *         data-za-action="Rate link click"
 *     >Mortgage rates</a>
 *
 *     <!-- Does not have to be an anchor! -->
 *     <div data-za-href="/mortgage-rates/"
 *         class="za-track-event"
 *         data-za-category="Mortgages"
 *         data-za-action="Rate link click"
 *     >Mortgage rates</div>
 *
 *     <!-- Inherit attributes from ancestors. -->
 *     <div data-za-category="Mortgages">
 *         <header data-za-label="Header">
 *             <a href="/mortgage-rates/"
 *                 class="za-track-event"
 *                 data-za-category="!inherit"
 *                 data-za-action="Rate link click"
 *                 data-za-label="!inherit"
 *             >Mortgage rates</a>
 *         </header>
 *         <footer data-za-label="Footer">
 *             <a href="/mortgage-rates/"
 *                 class="za-track-event"
 *                 data-za-category="!inherit"
 *                 data-za-action="Rate link click"
 *                 data-za-label="!inherit"
 *             >Mortgage rates</a>
 *         </footer>
 *     </div>
 *
 * @method handleTrackEventClick
 * @param {Event} e The click event
 * @param {Node} target The node that was clicked
 */
function handleTrackEventClick(e, target) {
    var event = {
        category: _getCategory(target),
        action: _getAction(target),
        label: _getLabel(target),
        value: _getValue(target),
        noninteraction: _getNoninteraction(target),
        href: _getHref(target),
        dimensions: _getDimensions(target),
        target: _getTarget(e, target),
    };

    if (typeof event.href !== 'undefined' && event.href !== null) {
        e.preventDefault();
    }

    trackEvent(event);
}

/**
 * Check if href is a tel link
 *
 * @method _isTelLink
 * @private
 */
function _isTelLink(href) {
    return /^tel:/i.test(href);
}

/**
 * Get the global Google Analytics object. This is generally window.ga,
 * but the global may be renamed on domains other than zillow.com. For
 * that reason, it is important to only call UA functions through the
 * object returned by this function.
 *
 * @method getGA
 * @return {Object} The GA global object.
 */
function getGA() {
    return typeof window !== 'undefined' && window[_globalName];
}

/**
 * Test to see if Google Universal Analytics is initialized.
 *
 * @method isInitialized
 */
function isInitialized() {
    return _isFunction(getGA());
}

/**
 * Guarded window.open redirect callback generator.
 *
 * @method _getRedirect
 * @param {Object} config
 * @param {String} config.href Redirect to URL after event fires
 * @param {String} config.target The URL target
 */
function _getRedirect(config) {
    if (typeof window !== 'undefined' && window.open) {
        return window.open.bind(window, config.href, config.target);
    }

    return logInfo.bind(null, 'ga redirect window.open("%s", "%s")', config.href, config.target);
}

/**
 * Extract the metrics and dimensions from the object provided to the track API
 * and inject the corresponding entries into the UA options provided.
 * @method _insertDimensionsAndMetrics
 * @private
 * @param {Object} googleOptions The options being prepared for a call to ga('send', ...)
 * @param {Object} trackingParams The options provided to the track...
 * API optionally containing dimensions/metrics objects
 */
function _insertDimensionsAndMetrics(googleOptions, trackingParams) {
    _insertEntries(googleOptions, 'dimension', trackingParams.dimensions);
    _insertEntries(googleOptions, 'metric', trackingParams.metrics);
}

/**
 * Add the values in the entries object to the UA options provided.
 * @method _insertEntries
 * @private
 * @param {Object} googleOptions The options being prepared for a call to ga('send', ...)
 * @param {String} prefix The type of option being added - 'dimension' | 'metric'
 * @param {Object} entries Mapping of int index to value for the dimension/metric to be set
 */
function _insertEntries(googleOptions, prefix, entries) {
    if (entries) {
        _assign(googleOptions, Object.keys(entries).reduce(reducer, {}));
    }

    function reducer(acc, key) {
        // Convert key to a number (strings that aren't numbers result in NaN)
        var index = Number(key);

        // ensure index is a positive integer
        if (index > 0 && index === Math.floor(index)) {
            acc[prefix + index] = entries[key];
        } else {
            logWarn('Could not set UA option ' + prefix + key);
        }

        return acc;
    }
}

/**
 * Retry a call to ZillowAnalytics object after a small wait
 * if ZillowAnalytics is not initialized when function is initially called.
 *
 * @param {function} fn
 * @param {number} maxRetryCount
 * @returns {void}
 */
function _zaWithRetry(fn, maxRetryCount) {
    if (typeof fn !== 'function') {
        logWarn('zaWithRetry was called without a function to retry.');
        return;
    }

    if (isZillowAnalyticsInitialized()) {
        fn();
    } else if (maxRetryCount > 0) {
        var retry = function zRetry() {
            _zaWithRetry(fn, maxRetryCount - 1);
        };

        setTimeout(retry, ZA_RETRY_AFTER_MS);
    } else {
        logWarn('ZillowAnalytics is not initialized causing a call to drop.');
    }
}

/**
 * Custom dimension Message UUID is being set for
 * Google Analytics and ZAnalytics. It helps to track potentially
 * lost events for both destinations
 */
var CustDimMessageUuid = 'dimension193';

/**
 * Sets dimension193 to unique id before sending track and page event
 * Original ticket: https://zbrt.atl.zillow.net/browse/DSP-2620
 * Fix ticket: https://zbrt.atl.zillow.net/browse/DSP-3046
 *
 * @param {any} obj an object to merge the custom dimension for unique event id onto
 * @param {string} uniqueEventId the unique event id (default: _uuid())
 * @param {string} objKey the object key to use (default: uses value from var CustDimMessageUuid)
 */
function _addUniqueEventIdDimension(obj, uniqueEventId, objKey) {
    uniqueEventId = uniqueEventId || _uuid();
    objKey = objKey || CustDimMessageUuid;

    var uniqueEventIdDim = {};
    uniqueEventIdDim[objKey] = uniqueEventId;

    obj = _merge(obj, uniqueEventIdDim);
}

/**
 * Default analytics event tracking.
 * This may be overriden.
 *
 * @method _trackEvent
 * @private
 * @param {Object} config
 */
function _trackEvent(config) {
    var googleUAObj = {
        hitType: 'event',
        eventCategory: config.category,
        eventAction: config.action,
        eventLabel: config.label,
        eventValue: config.value,
        nonInteraction: config.noninteraction,
    };

    var uniqueEventId = _uuid();

    if (config.hitCallback) {
        googleUAObj.hitCallback = config.hitCallback;
    }

    if (isInitialized()) {
        _insertDimensionsAndMetrics(googleUAObj, config);
        _addUniqueEventIdDimension(googleUAObj, uniqueEventId);
        getGA()('send', googleUAObj);
    } else {
        logWarn('Would send event to GA but GA not initialized: ', googleUAObj);
    }

    var zaObj = {
        category: config.category,
        action: config.action,
        label: config.label,
        value: config.value,
        nonInteraction: config.noninteraction,
    };
    var zaOpts = {};
    _insertDimensionsAndMetrics(zaObj, config);
    _addUniqueEventIdDimension(zaOpts, uniqueEventId, 'uniqueEventId');

    var zaFn = function zaRetryFn() {
        getZA().track('event', zaObj, zaOpts);
    };
    _zaWithRetry(zaFn, ZA_RETRY_MAX_COUNT);
}

/**
 * Track a Goggle Analytics event
 * (<a href="https://zwiki.zillowgroup.net/display/uxdev/Google+Analytics">read more about the GA tracking</a>).
 *
 * ### Examples
 *
 *     import { trackEvent } from '@zillow/universal-analytics';
 *
 *     // Track an event
 *     trackEvent({
 *         category: 'Mortgages',
 *         action: 'Upsell clicked'
 *         label: 'Search results list'
 *     });
 *
 *     // Track an event and then redirect
 *     trackEvent({
 *         category: 'Mortgages',
 *         action: 'Upsell clicked'
 *         label: 'Search results list',
 *         href: 'http://www.zillow.com/mortgage-rates/'
 *     });
 *
 *     // Track an event and open url in a new window
 *     trackEvent({
 *         category: 'Mortgages',
 *         action: 'Upsell clicked'
 *         label: 'Search results list',
 *         href: 'http://www.zillow.com/mortgage-rates/',
 *         target: '_blank'
 *     });
 *
 * @method trackEvent
 * @param {Object} config
 * @param {String} config.category Event category
 * @param {String} config.action Event action
 * @param {String} [config.label] Event label
 * @param {Number} [config.value] Event value
 * @param {Boolean} [config.noninteraction] Noninteraction event
 * @param {String} [config.href] Redirect to URL after event fires
 * @param {String} [config.target] The URL target, default: '_self'
 */
function trackEvent(config) {
    config = config || {};

    // Handle href redirects
    if (config.href) {
        // eslint-disable-next-line no-param-reassign
        config.target = config.target || '_self';

        // Bind href and target to the open function so it can be used
        // with setTimeout.
        var redirect = _getRedirect(config);

        // Not necessary to wait for URLs opened in a new target, and
        // waiting will cause the URL to be opened in a window instead
        // of a tab because it is no longer "user-initiated".
        if (config.target === '_blank' || _isTelLink(config.href)) {
            redirect.call();
        } else if (isInitialized()) {
            // When GA is intitialized, the redirect will occur when the
            // hit callback is called (usually very fast). If for some
            // reason GA fails or is really slow, the timeout will
            // guarantee the redirect occurs in a timely manner.
            var timeout = setTimeout(redirect, 500);

            // eslint-disable-next-line no-param-reassign
            config.hitCallback = function callRedirect() {
                // Make sure to clear timeout as anchor hrefs do not
                // change the page.
                clearTimeout(timeout);
                redirect.call();
            };
        } else {
            // Old school redirect in case GA hasn't been initialized
            setTimeout(redirect, 100);
        }
    }

    // Category and action are required fields
    if (!config.category) {
        logWarn('trackEvent: missing category');
    } else if (!config.action) {
        logWarn('trackEvent: missing action');
    } else if (nativeApi.shouldDispatchToNative()) {
        nativeApi.trackEvent(config);
    } else {
        _trackEvent(config);
    }
}

/**
 * trackEventV2 is partially backward compatible with @see trackEvent
 * breaking change: hitCallback moved from the first function parameter
 * (properties) to the second (options).
 * V2 designed to pass more optional data to zanalytics (or other SDKs).
 * Example:
 *
 *     const { trackEventV2 } = require('@zillow/universal-analytics');
 *     const props = { category: 'Cat', action: 'ate', label: 'Dog'};
 *     const options = { hitCallback: () => alert('I use sample code in prod!'), dimensions: { }, };
 *     trackEventV2(props, options)
 *
 * This method support event routing via `options.integrations`
 *
 *     var props = { category: 'Cat', action: 'Act', label: 'Lab'};
 *     var opts = {
 *         integrations: {
 *             All: false,
 *         }
 *     };
 *
 *     // Dry run, don't send event
 *     trackEventV2(props, opts);
 *
 *     opts = {
 *         integrations: {
 *             All: false,
 *             GoogleAnalytics: true,
 *         }
 *     };
 *
 *     // Send Only to GoogleAnalytics
 *     trackEventV2(props, opts);
 *
 *     opts = {
 *         integrations: {
 *             All: true,
 *             GoogleAnalytics: false,
 *         }
 *     };
 *
 *     // Send to all, except GoogleAnalytics
 *     trackEventV2(props, opts);
 *
 * @param {Object} properties
 * @param {String} properties.category Event category
 * @param {String} properties.action Event action
 * @param {String}   [properties.label] Event label
 * @param {Number}   [properties.value] Event value
 * @param {Boolean}  [properties.noninteraction] Noninteraction event
 * @param {Object}   [options]
 * @param {Object}   [options.integrations]
 * @param {Object}   [options.dimensions]
 * @param {Object}   [options.metrics]
 * @param {Function} [options.hitCallback] hitCallback
 */
function trackEventV2(properties, options) {
    var props = properties || {};
    var opts = options || {};

    var uniqueEventId = _uuid();

    if (nativeApi.shouldDispatchToNative()) {
        nativeApi.trackEvent({
            category: props.category,
            action: props.action,
            label: props.label,
            value: props.value,
            noninteraction: props.noninteraction,
        });
        return;
    }

    var googleUAObj = _formatGaEventPayloadV2(props);
    googleUAObj.hitCallback = opts.hitCallback;
    delete opts.hitCallback;
    _insertDimensionsAndMetrics(googleUAObj, opts);
    _addUniqueEventIdDimension(googleUAObj, uniqueEventId);

    if (isInitialized()) {
        getGA()('send', googleUAObj);
    } else {
        logWarn('Would send event to GA but GA not initialized: ', googleUAObj);
    }

    var zaObj = _formatZaEventPayloadV2(props);
    _insertDimensionsAndMetrics(zaObj, opts);
    _addUniqueEventIdDimension(opts, uniqueEventId, 'uniqueEventId');

    var zaFn = function zaRetryFn() {
        getZA().track('event', zaObj, opts);
    };
    _zaWithRetry(zaFn, ZA_RETRY_MAX_COUNT);
}

/**
 * Helper function for @see trackEventV2
 * @param {Object} props
 */
function _formatGaEventPayloadV2(props) {
    var googleUAObj = {
        hitType: 'event',
        eventCategory: props.category,
        eventAction: props.action,
        eventLabel: props.label,
        eventValue: props.value,
        nonInteraction: props.noninteraction,
    };
    return googleUAObj;
}

/**
 * Helper function for @see trackEventV2
 * @param {Object} props
 */
function _formatZaEventPayloadV2(props) {
    var zaPayload = _assign({}, props);
    zaPayload.nonInteraction = zaPayload.noninteraction;
    delete zaPayload.noninteraction;
    return zaPayload;
}

/**
 * Identifies logged in user with userId
 *
 * ### Example
 *
 *     import { identifyUser } from '@zillow/universal-analytics';
 *
 *     // Track a logged in user
 *     var userId = 'X1-ZU12u7tvmlvs0ll_2ca41';
 *     identifyUser({
 *         userId
 *     });
 *
 * @method identifyUser
 * @param {Object} config
 * @param {String} config.userId
 *
 * Please see also:
 * [Cookies and User Identification](https://developers.google.com/analytics/devguides/collection/analyticsjs/cookies-user-id)
 * Section: Handling authentication after pageload
 */
function identifyUser(config) {
    if (!config) {
        return;
    }
    if (nativeApi.shouldDispatchToNative()) {
        // 2019-01-07 @dmytroz:
        // Implementing identify in ZMOB_nativeAPI requires some additional research
        // I've decided it would be safe to disable this function in PageView
        // https://bitbucket.zgtools.net/projects/MB/repos/mobile_apps_zillowmap.ios/browse/Pods/ZCommonUI/ZCommonUIResources/hdp-native-interface.js#5,285
        logWarn('identifyUser: DispatchToNative is not implemented');
    } else {
        if (isInitialized()) {
            // 2019-01-08 dmytroz:
            // GA is not implemented, please see our discussion at:
            // https://bitbucket.zgtools.net/projects/FE/repos/universal-analytics/pull-requests/5/overview
            logWarn('identifyUser: GA is not implemented');
        }

        var zaFn = function zaRetryFn() {
            getZA().identify(config.userId);
        };
        _zaWithRetry(zaFn, ZA_RETRY_MAX_COUNT);
    }
}

/**
 * Track a Google Analytics page view.
 *
 * ### Example
 *
 *     import { trackPageview } from '@zillow/universal-analytics';
 *
 *     // Track a page view
 *     trackPageview({
 *         pageURL: '/postings/sign-in/'
 *     });
 *
 * @method trackPageview
 * @param {Object} [config]
 * @param {String} [config.pageURL] Page URL to track metrics under
 */
function trackPageview(config) {
    // eslint-disable-next-line no-param-reassign
    config = config || {};

    var uniqueEventId = _uuid();

    var googleUAObj = {
        hitType: 'pageview',
        page: config.pageURL,
    };

    if (!config.pageURL) {
        logWarn('trackPageview: missing pageURL');
    } else if (nativeApi.shouldDispatchToNative()) {
        nativeApi.trackPageView(config);
    } else if (isInitialized()) {
        _addUniqueEventIdDimension(googleUAObj, uniqueEventId);
        getGA()('send', googleUAObj);
    } else {
        logWarn('Would send page to GA but GA not initialized: ', googleUAObj);
    }

    var zaOpts = {};
    _addUniqueEventIdDimension(zaOpts, uniqueEventId, 'uniqueEventId');

    var zaFn = function zaRetryFn() {
        getZA().page(config.pageURL, null, zaOpts);
    };
    _zaWithRetry(zaFn, ZA_RETRY_MAX_COUNT);
}

/**
 * trackPageviewV2 is backward compatible with @see trackPageview
 * V2 designed to pass more optional data to zanalytics (or other SDKs)
 *
 * @param {Object} properties
 * @param {string} properties.pageURL
 * @param {Object} [options]
 */
function trackPageviewV2(properties, options) {
    var props = properties || {};
    var opts = options || {};
    // eslint-disable-next-line prefer-destructuring
    var pageURL = props.pageURL;
    delete props.pageURL;

    var uniqueEventId = _uuid();

    if (nativeApi.shouldDispatchToNative()) {
        nativeApi.trackPageView({
            pageURL: pageURL,
        });
        return;
    }

    var googleUAObj = {
        hitType: 'pageview',
        page: pageURL,
    };

    if (isInitialized()) {
        _addUniqueEventIdDimension(googleUAObj, uniqueEventId);
        getGA()('send', googleUAObj);
    } else {
        logWarn('Would send page to GA but GA not initialized: ', googleUAObj);
    }

    _addUniqueEventIdDimension(opts, uniqueEventId, 'uniqueEventId');

    var zaFn = function zaRetryFn() {
        getZA().page(pageURL, props, opts);
    };
    _zaWithRetry(zaFn, ZA_RETRY_MAX_COUNT);
}

/**
 * Set custom dimensions
 *
 * ### Example
 *
 *     import { setCustomDimensions } from '@zillow/universal-analytics';
 *
 *     // set some custom dimensions
 *     setCustomDimensions({
 *         dimension5: 'foo',
 *         dimension10: 'bar
 *     });
 *
 * @method setCustomDimensions
 * @param {object} dimensionsObj Keyed by dimension name
 */
function setCustomDimensions(dimensionsObj) {
    if (nativeApi.shouldDispatchToNative()) {
        nativeApi.setCustomDimensions(dimensionsObj);
        return;
    }

    if (isInitialized()) {
        getGA()('set', dimensionsObj);
    } else {
        logWarn('Would set custom dimensions in GA but GA not initialized: ', dimensionsObj);
    }

    var zaFn = function zaRetryFn() {
        if (getZA() && typeof getZA().setdim === 'function') {
            getZA().setdim(dimensionsObj);
        }
    };
    _zaWithRetry(zaFn, ZA_RETRY_MAX_COUNT);
}

/**
 * Manually request and initialize the Universal Analytics JavaScript
 * snippet. This is NOT required if you are using Border.
 *
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/advanced
 *
 * @method requestUAScript
 * @param {String} globalName The global UA object name.
 */
function requestUAScript(globalName) {
    _globalName = globalName || _globalName;
    /* eslint-disable */
    // prettier-ignore
    (function (i, s, o, g, r, a, m) {
        i['GoogleAnalyticsObject'] = r; i[r] = i[r] || function () {
            (i[r].q = i[r].q || []).push(arguments);
        }; i[r].l = 1 * new Date(); a = s.createElement(o);
        m = s.getElementsByTagName(o)[0]; a.async = 1; a.src = g; m.parentNode.insertBefore(a, m);
    })(window, document, 'script', '//www.google-analytics.com/analytics.js', _globalName);
    /* eslint-enable */
}

/**
 * Applies any passed arguments to the ga function.
 *
 * @method gaCommand
 */
function gaCommand() {
    if (isInitialized()) {
        getGA().apply(window, [].slice.call(arguments));
    } else {
        logWarn('gaCommand called, but UA was not initialized!');
    }
}

/**
 * Initialize the global click listener. The listener will handle all
 * clicks on elements with the class `za-track-event`.
 *
 * @method initGlobalClickListener
 */
function initGlobalClickListener() {
    if (typeof document !== 'undefined' && !_initialized) {
        delegate(document.body, 'click', handleTrackEventClick, '.za-track-event');
        _initialized = true;
    }
}

/**
 * Find out if zillow analytics script is initialized
 * Also make sure that window.analytics is not a HTML element
 *
 * @method isZillowAnalyticsInitialized
 */
function isZillowAnalyticsInitialized() {
    return (
        typeof window !== 'undefined' &&
        typeof getZA() !== 'undefined' &&
        typeof getZA().nodeName === 'undefined'
    );
}

/**
 * Default analytics Ecommerce AddImpression.
 *
 * @method ecAddImpression
 * @public
 * @param {Object} [config]
 * @param {String} [config.id] id of the product, here its zpid. A mandatory field.
 * @param {String} [config.brand] Product Brand.
 * @param {String} [config.list] Product list.
 * @param {int} [config.position] Product position.
 * @param {String} [config.name] name of the product.
 * @param {String} [config.variant] variant of the product like black.
 * @param {String} [config.category] category to which the product belongs.
 */
function ecAddImpression(config) {
    var googleECObj = {
        id: config.id,
    };

    if (config.list) {
        googleECObj.list = config.list;
    }

    if (config.brand) {
        googleECObj.brand = config.brand;
    }

    if (config.position) {
        googleECObj.position = config.position;
    }

    if (config.name) {
        googleECObj.name = config.name;
    }

    if (config.variant) {
        googleECObj.variant = config.variant;
    }

    if (config.category) {
        googleECObj.category = config.category;
    }

    if (!config.id) {
        logWarn('ec:addImpression: missing id');
    } else if (isInitialized()) {
        getGA()('ec:addImpression', googleECObj);
    } else {
        logWarn('ecAddImpression called but UA is not initialized: ', googleECObj);
    }
}

/**
 * Analytics eCommerce AddProduct.
 *
 * @method ecAddProduct
 * @public
 *
 * @param {Object} [config]
 * @param {String} [config.id] id of the product, here its zpid. A mandatory field.
 * @param {String} [config.brand] Product Brand.
 * @param {int} [config.position] Product position.
 * @param {String} [config.name] name of the product.
 * @param {String} [config.variant] variant of the product like black.
 * @param {String} [config.category] category to which the product belongs.
 */
function ecAddProduct(config) {
    var googleECObj = {
        id: config.id,
    };

    if (config.brand) {
        googleECObj.brand = config.brand;
    }

    if (config.position) {
        googleECObj.position = config.position;
    }

    if (config.name) {
        googleECObj.name = config.name;
    }

    if (config.variant) {
        googleECObj.variant = config.variant;
    }

    if (config.category) {
        googleECObj.category = config.category;
    }

    if (!config.id) {
        logWarn('ec:addProduct: missing id');
    } else if (isInitialized()) {
        getGA()('ec:addProduct', googleECObj);

        if (config.click && config.list) {
            getGA()('ec:setAction', 'click', {
                list: config.list,
            });
        }

        if (config.detail) {
            getGA()('ec:setAction', config.detail);
        }
    } else {
        logWarn('ecAddProduct called but UA is not initialized : ', googleECObj);
    }
}

/**
 * Get the global Zillow Analytics object. This is generally window.analytics,
 * but the global may be renamed on domains other than zillow.com.
 *
 * @method getZA
 * @return {Object} The Zillow Analytics global object.
 */
function getZA() {
    return typeof window !== 'undefined' && window[window.ZillowAnalyticsObject];
}
